import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, FileSpreadsheet, Plus, Upload } from 'lucide-react';
import api from '../services/api';
import BatchUploadSection from '../components/upload/BatchUploadSection';
import SingleUpload from './SingleUpload';
import '../styles/upload-page.css';

const CSV_TEMPLATE_KEY = 'resources/submission-metadata-template.csv';

const TrackUpload = () => {
  const [searchParams] = useSearchParams();
  const [showSingle, setShowSingle] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [incomingCsvFile, setIncomingCsvFile] = useState(null);
  const topCsvInputRef = useRef(null);

  useEffect(() => {
    if (searchParams.get('mode') === 'single') {
      setShowSingle(true);
    }
  }, [searchParams]);

  const handleTopCsvFile = (file) => {
    if (!file?.name?.toLowerCase().endsWith('.csv')) return;
    setIncomingCsvFile(file);
  };

  const onTopCsvChange = (event) => {
    const file = event.target.files?.[0];
    if (file) handleTopCsvFile(file);
    event.target.value = '';
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    handleTopCsvFile(file);
  };

  const handleDownloadTemplate = async (event) => {
    event.preventDefault();
    try {
      const { data } = await api.get('/api/media/presign', {
        params: { url: CSV_TEMPLATE_KEY },
      });
      if (!data.presignedUrl) throw new Error('No download URL returned');
      window.location.assign(data.presignedUrl);
    } catch (error) {
      console.error('Template download error:', error);
    }
  };

  return (
    <div className="upload-page">
      <div className="upload-page-scroll">
        <div className="space-y-6 fade-in">
          <section className="space-y-4">
            <div className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">DATA ENTRY</div>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white">Add to a Catalog</h1>
              <div className="flex items-center gap-2">
                <input
                  ref={topCsvInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={onTopCsvChange}
                />
                <button
                  type="button"
                  onClick={() => topCsvInputRef.current?.click()}
                  className="bg-transparent border border-white/30 text-white/70 text-xs font-medium px-3 py-2 rounded uppercase tracking-wider hover:border-white hover:text-white hover:bg-white/[0.06] transition-all duration-200 flex items-center gap-2"
                >
                  <Upload size={14} /> Import CSV
                </button>
                <button
                  type="button"
                  onClick={() => setShowSingle(true)}
                  className="bg-transparent border border-white/30 text-white/70 text-xs font-medium px-3 py-2 rounded uppercase tracking-wider hover:border-white hover:text-white hover:bg-white/[0.06] transition-all duration-200 flex items-center gap-2"
                >
                  <Plus size={14} /> Add Single Track
                </button>
              </div>
            </div>
          </section>

          <div
            onDragOver={(event) => {
              event.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`rounded-lg flex items-center justify-center gap-4 border border-dashed transition-all duration-300 py-6 px-8 ${
              dragOver
                ? 'border-[#C4AB04] bg-[#C4AB04]/[0.06] scale-[1.01]'
                : 'border-white/15 bg-black/60 hover:border-white/25'
            }`}
          >
            <FileSpreadsheet
              size={24}
              className={`transition-colors duration-300 shrink-0 ${dragOver ? 'text-[#C4AB04]' : 'text-gray-500'}`}
            />
            <div className="text-sm font-medium text-white/70">Drop a CSV here to bulk import</div>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] rounded border border-white/15 text-gray-400 hover:text-white hover:border-white/30 hover:bg-white/[0.04] transition-all duration-200"
            >
              <Download size={11} /> Template
            </button>
          </div>

          <section className="space-y-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-white">Batch Upload</div>
              <p className="text-xs text-gray-400 mt-1">
                Upload a CSV metadata file and matching audio and image files
              </p>
            </div>
            <BatchUploadSection
              embedded
              hideCsvDropzone
              incomingCsvFile={incomingCsvFile}
              onIncomingCsvConsumed={() => setIncomingCsvFile(null)}
            />
          </section>

          {showSingle && (
            <section className="bg-black/90 rounded-lg shadow-[0_4px_4px_rgba(0,0,0,0.17)] overflow-hidden">
              <SingleUpload embedded onClose={() => setShowSingle(false)} />
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrackUpload;
